/**
 * Factsheet — หลักการคำนวณของ PayPilot.
 *
 * เป็น user-facing transparency document — บอกหลักการ + สูตร + ตัวอย่าง +
 * อ้างอิงกฎหมาย ของทุกการคำนวณในระบบ. ไม่ใช่ dev docs (อันนั้น =
 * docs/PAYROLL_FORMULAS.md). ทุก role อ่านได้.
 *
 * Phase 1: ส่วน A ภาษีหัก ณ ที่จ่าย (ทำหลัง compliance fix 2026-05-25).
 * Phase 2: B-J — SSO, PF, OT, severance, leave, etc.
 */
import { useEffect } from 'react';
import { PageLayout } from '../components/layout/PageLayout';
import { Link } from 'react-router-dom';
import { ScrollText, Calculator, Scale, ExternalLink, Shield, PiggyBank, Clock, Zap, Moon, Plane, UserMinus, Scissors, History } from 'lucide-react';

interface Section {
  id: string;
  title: string;
  status: 'live' | 'planned';
}

const SECTIONS: Section[] = [
  { id: 'tax', title: 'A. ภาษีหัก ณ ที่จ่าย (Cumulative Average)', status: 'live' },
  { id: 'sso', title: 'B. ประกันสังคม (อัตรา / เพดาน)', status: 'live' },
  { id: 'pf', title: 'C. กองทุนสำรองเลี้ยงชีพ', status: 'live' },
  { id: 'daily-rate', title: 'D. ค่าจ้างรายวัน / รายชั่วโมง', status: 'live' },
  { id: 'ot', title: 'E. ค่าล่วงเวลา (OT) — 3 ประเภท', status: 'live' },
  { id: 'night', title: 'F. เบี้ยกะกลางคืน', status: 'live' },
  { id: 'leave', title: 'G. การลา (โควต้า + กฎปีแรก §30)', status: 'live' },
  { id: 'severance', title: 'H. ค่าชดเชยเลิกจ้าง §118', status: 'live' },
  { id: 'deductions', title: 'I. เงินหัก §76 (เพดาน 10%)', status: 'live' },
  { id: 'ytd', title: 'J. ยอด YTD + true-up เดือน ธ.ค.', status: 'live' },
];

export function Factsheet() {
  // Scroll to anchor on first load (deep-link จาก slip/year-end)
  useEffect(() => {
    if (!window.location.hash) return;
    const id = window.location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  }, []);

  return (
    <PageLayout title="หลักการคำนวณของ PayPilot">
      {/* Intro */}
      <div className="card p-4 mb-4 bg-brand-50 border-brand-200">
        <div className="flex items-start gap-3">
          <ScrollText className="w-5 h-5 text-brand-700 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="text-gray-700 leading-relaxed">
              เอกสารนี้บอก<strong>หลักการที่ระบบใช้คำนวณ</strong>ทุกตัวเลขในสลิป —
              ทั้งภาษี ประกันสังคม OT ค่าชดเชย ฯลฯ —
              พร้อมอ้างอิงมาตรากฎหมายให้ตรวจสอบได้
            </p>
            <p className="text-xs text-gray-500 mt-2">
              อัปเดต: 25 พ.ค. 2569 · ใช้สำหรับปีภาษี 2569 ·
              รีวิวโดย Phusita (HR & Payroll, Moodata)
            </p>
          </div>
        </div>
      </div>

      {/* TOC */}
      <div className="card p-4 mb-6">
        <div className="font-semibold mb-2 text-sm text-gray-700">สารบัญ</div>
        <ol className="space-y-1 text-sm">
          {SECTIONS.map((s) => (
            <li key={s.id}>
              {s.status === 'live' ? (
                <a href={`#${s.id}`} className="text-brand-700 hover:underline">
                  {s.title}
                </a>
              ) : (
                <span className="text-gray-400">{s.title} <span className="text-xs">(เร็วๆ นี้)</span></span>
              )}
            </li>
          ))}
        </ol>
      </div>

      <SectionTax />
      <SectionSSO />
      <SectionPF />
      <SectionDailyRate />
      <SectionOT />
      <SectionNight />
      <SectionLeave />
      <SectionSeverance />
      <SectionDeductions />
      <SectionYTD />

      <div className="mt-6 text-center">
        <Link to="/profile" className="text-sm text-gray-500 hover:text-gray-700">
          ← กลับไปประวัติของฉัน
        </Link>
      </div>
    </PageLayout>
  );
}

// Shared building blocks — แต่ละ section ใช้รูปแบบเดียวกัน (หลักการ + สูตร +
// ตัวอย่าง + อ้างอิงกฎหมาย) เพื่อให้ผู้อ่านสแกนข้ามตอนได้ง่าย
function SectionCard({ id, icon, title, children }: { id: string; icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-20">
      <div className="card p-5 mb-4">
        <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
          {icon}
          {title}
        </h2>
        {children}
      </div>
    </section>
  );
}

function H3({ children }: { children: React.ReactNode }) {
  return <h3 className="font-semibold mt-4 mb-1">{children}</h3>;
}

function Code({ children }: { children: string }) {
  return (
    <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-x-auto leading-relaxed">
      {children}
    </pre>
  );
}

function Refs({ items }: { items: React.ReactNode[] }) {
  return (
    <>
      <H3>อ้างอิงกฎหมาย</H3>
      <ul className="text-sm text-gray-600 list-disc pl-6 space-y-0.5">
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </>
  );
}

function SectionTax() {
  return (
    <section id="tax" className="scroll-mt-20">
      <div className="card p-5 mb-4">
        <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
          <Calculator className="w-5 h-5 text-brand-700" />
          A. ภาษีหัก ณ ที่จ่าย (เงินเดือน)
        </h2>

        {/* หลักการ */}
        <h3 className="font-semibold mt-4 mb-1">หลักการ</h3>
        <p className="text-sm text-gray-700 leading-relaxed">
          ใช้วิธี <strong>เฉลี่ยสะสมตั้งแต่ต้นปี (Cumulative-Average)</strong> ตามแนวปฏิบัติของกรมสรรพากร —
          ทุกเดือนจะ <em>ประมาณการเงินได้ทั้งปี</em> จากยอดสะสมจริงถึงเดือนนั้น
          แล้วคำนวณภาษีที่ควรหักสะสมถึงเดือนนี้ หักลบกับที่หักไปแล้ว
          ส่วนต่างคือยอดที่หักเดือนนี้
        </p>
        <p className="text-sm text-gray-600 mt-2 italic">
          ผลลัพธ์: เดือนที่มีโบนัส/OT/คอม → ยอดสะสมพุ่ง → เดือนนั้นหักมากขึ้น
          แล้วเดือนถัดไปปรับลง — ยอดสะสมทั้งปีจึงตรงกับภาษีจริง
          ลดการคืน/จ่ายเพิ่มก้อนใหญ่ตอนยื่น ภงด.91
        </p>

        {/* สูตร */}
        <h3 className="font-semibold mt-5 mb-1">สูตร (m = เดือนที่ของปี, 1=ม.ค.)</h3>
        <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-x-auto leading-relaxed">
{`เงินได้สะสม         = เงินได้สะสมก่อนหน้า + ฐานภาษีเดือนนี้
ประมาณการเงินได้ทั้งปี = เงินได้สะสม × 12 / m

หักค่าลดหย่อน (ทั้งปี):
  − ค่าลดหย่อนส่วนตัว                  60,000
  − ค่าใช้จ่าย = min(เงินได้ × 50%, 100,000)   ← ม.40(1)
  − ประกันสังคม = SSO_สะสมจริง × 12 / m
  − กองทุน PF = min(จ่ายจริง, เงินได้×15%, 500,000)  ← ม.65 ตรี
                                       ─────────
เงินได้สุทธิ          = ประมาณการ − ลดหย่อน (≥ 0)
ภาษีทั้งปี           → ขั้นบันได (ดูตารางด้านล่าง)
ภาษีที่ควรหักสะสม     = ภาษีทั้งปี × m / 12
ภาษีหักเดือนนี้      = (ที่ควรหักสะสม) − (หักไปแล้ว) ≥ 0`}
        </pre>

        {/* ตารางลดหย่อน */}
        <h3 className="font-semibold mt-5 mb-2">ค่าลดหย่อนที่ระบบหักให้</h3>
        <div className="overflow-x-auto">
          <table className="text-sm w-full border-collapse">
            <thead className="bg-gray-50 text-xs text-gray-600">
              <tr>
                <th className="border border-gray-200 px-2 py-1.5 text-left">รายการ</th>
                <th className="border border-gray-200 px-2 py-1.5 text-left">ค่า / สูตร</th>
                <th className="border border-gray-200 px-2 py-1.5 text-left">อ้างอิง</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              <tr>
                <td className="border border-gray-200 px-2 py-1.5">ค่าลดหย่อนส่วนตัว</td>
                <td className="border border-gray-200 px-2 py-1.5">60,000 บาท/ปี</td>
                <td className="border border-gray-200 px-2 py-1.5 text-xs text-gray-500">ปกร. ปีภาษีปัจจุบัน</td>
              </tr>
              <tr>
                <td className="border border-gray-200 px-2 py-1.5">ค่าใช้จ่าย</td>
                <td className="border border-gray-200 px-2 py-1.5">min(เงินได้ × 50%, 100,000)</td>
                <td className="border border-gray-200 px-2 py-1.5 text-xs text-gray-500">ม.40(1)</td>
              </tr>
              <tr>
                <td className="border border-gray-200 px-2 py-1.5">ประกันสังคม (SSO)</td>
                <td className="border border-gray-200 px-2 py-1.5">ยอดสะสมจริง × 12 / m</td>
                <td className="border border-gray-200 px-2 py-1.5 text-xs text-gray-500">พ.ร.บ.ปกส.</td>
              </tr>
              <tr>
                <td className="border border-gray-200 px-2 py-1.5">กองทุน PF</td>
                <td className="border border-gray-200 px-2 py-1.5">≤ 15% ของเงินได้ AND ≤ 500,000</td>
                <td className="border border-gray-200 px-2 py-1.5 text-xs text-gray-500">ม.65 ตรี</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ขั้นบันได */}
        <h3 className="font-semibold mt-5 mb-2">ขั้นบันไดภาษี (Progressive — คิดเฉพาะส่วนในขั้นนั้น)</h3>
        <div className="overflow-x-auto">
          <table className="text-sm w-full border-collapse">
            <thead className="bg-gray-50 text-xs text-gray-600">
              <tr>
                <th className="border border-gray-200 px-2 py-1.5 text-left">เงินได้สุทธิ (บาท/ปี)</th>
                <th className="border border-gray-200 px-2 py-1.5 text-left">อัตรา</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              {[
                ['0 – 150,000', '0%'],
                ['150,001 – 300,000', '5%'],
                ['300,001 – 500,000', '10%'],
                ['500,001 – 750,000', '15%'],
                ['750,001 – 1,000,000', '20%'],
                ['1,000,001 – 2,000,000', '25%'],
                ['2,000,001 – 5,000,000', '30%'],
                ['5,000,000 ขึ้นไป', '35%'],
              ].map(([range, rate]) => (
                <tr key={range}>
                  <td className="border border-gray-200 px-2 py-1.5">{range}</td>
                  <td className="border border-gray-200 px-2 py-1.5 font-mono">{rate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ตัวอย่าง */}
        <h3 className="font-semibold mt-5 mb-1">ตัวอย่าง — เงินเดือน 50,000 คงที่</h3>
        <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-x-auto leading-relaxed">
{`เดือน ม.ค. (m=1):
  เงินได้สะสม          = 50,000
  ประมาณการทั้งปี     = 50,000 × 12/1 = 600,000
  ค่าใช้จ่าย          = min(600,000 × 50%, 100,000) = 100,000
  ลดหย่อนรวม         = 60,000 + 100,000 + (875×12=10,500) = 170,500
  เงินได้สุทธิ        = 600,000 − 170,500 = 429,500

ภาษีทั้งปี:
   150,000 แรก × 0%   = 0
   150,000 ถัดไป × 5% = 7,500
   129,500 ถัดไป × 10% = 12,950
   รวม                = 20,450

ควรหักสะสมถึง ม.ค. = 20,450 × 1/12 = 1,704
หักไปแล้ว           = 0
► ภาษี ม.ค.        = 1,704 บาท`}
        </pre>
        <p className="text-xs text-gray-500 mt-2">
          (เงินเดือนสม่ำเสมอ — ภาษีรายเดือนคงที่ ~1,704 บาททุกเดือน เดือน ธ.ค. true-up อัตโนมัติ)
        </p>

        {/* ที่ระบบไม่ทำให้ */}
        <h3 className="font-semibold mt-5 mb-1 flex items-center gap-2">
          <Scale className="w-4 h-4 text-amber-700" />
          สิ่งที่ระบบ <span className="text-amber-700">ไม่หัก</span> ให้ (พนักงานต้องขอคืนเอง)
        </h3>
        <p className="text-sm text-gray-700 leading-relaxed">
          ระบบหักภาษีแบบ <strong>อนุรักษ์</strong> — หักเฉพาะลดหย่อนพื้นฐาน
          (ส่วนตัว 60k + ค่าใช้จ่าย + SSO + PF) ไม่รวม:
        </p>
        <ul className="text-sm text-gray-700 list-disc pl-6 mt-1 space-y-0.5">
          <li>คู่สมรส / บุตร / บิดามารดา</li>
          <li>เบี้ยประกันชีวิต / สุขภาพ</li>
          <li>RMF / SSF / TESG</li>
          <li>ดอกเบี้ยกู้บ้าน / เงินบริจาค</li>
        </ul>
        <p className="text-sm text-gray-700 mt-2">
          ถ้าคุณมีรายการเหล่านี้ → จะถูกหักภาษีเกินตลอดปี
          แล้ว<strong>ขอคืนตอนยื่น ภงด.91</strong> ปลายปี (เม.ย. ปีถัดไป)
        </p>

        {/* อ้างอิง */}
        <h3 className="font-semibold mt-5 mb-1">อ้างอิงกฎหมาย</h3>
        <ul className="text-sm text-gray-600 list-disc pl-6 space-y-0.5">
          <li>ประมวลรัษฎากร <strong>ม.40(1)</strong> — เงินได้ประเภทเงินเดือน + ค่าใช้จ่าย 50% เพดาน 100,000</li>
          <li>ประมวลรัษฎากร <strong>ม.50(1)</strong> — นายจ้างต้องหัก ณ ที่จ่าย</li>
          <li>ประมวลรัษฎากร <strong>ม.65 ตรี (3)</strong> — กองทุนสำรองเลี้ยงชีพ ≤ 15% + 500,000</li>
          <li>วิธีเฉลี่ยสะสม — แนวปฏิบัติของกรมสรรพากร <a href="https://www.rd.go.th" target="_blank" rel="noreferrer" className="text-brand-700 hover:underline inline-flex items-center gap-0.5">rd.go.th <ExternalLink className="w-3 h-3" /></a></li>
        </ul>
      </div>
    </section>
  );
}

// === B. ประกันสังคม ===
function SectionSSO() {
  return (
    <SectionCard id="sso" icon={<Shield className="w-5 h-5 text-brand-700" />} title="B. ประกันสังคม (SSO)">
      <H3>หลักการ</H3>
      <p className="text-sm text-gray-700">
        ลูกจ้างและนายจ้างจ่ายฝ่ายละ <strong>5% ของค่าจ้าง</strong> เข้ากองทุนประกันสังคม
        โดยคิดจากฐานค่าจ้างไม่เกินเพดานที่กฎหมายกำหนด — ส่วนเกินเพดานไม่นำมาคำนวณ
      </p>

      <H3>สูตร</H3>
      <Code>{`SSO = min(ค่าจ้างเดือนนี้, ceiling) × 5%
     = min(min(ค่าจ้าง × 5%), max_monthly)`}</Code>

      <H3>เพดาน (version-aware ตามปีภาษี)</H3>
      <div className="overflow-x-auto">
        <table className="text-sm w-full border-collapse">
          <thead className="bg-gray-50 text-xs text-gray-600">
            <tr>
              <th className="border border-gray-200 px-2 py-1.5 text-left">ปี</th>
              <th className="border border-gray-200 px-2 py-1.5 text-left">เพดานค่าจ้าง</th>
              <th className="border border-gray-200 px-2 py-1.5 text-left">เพดาน SSO/เดือน</th>
            </tr>
          </thead>
          <tbody className="text-gray-700">
            <tr><td className="border border-gray-200 px-2 py-1.5">2568</td><td className="border border-gray-200 px-2 py-1.5">15,000</td><td className="border border-gray-200 px-2 py-1.5 font-mono">750</td></tr>
            <tr className="bg-brand-50"><td className="border border-gray-200 px-2 py-1.5">2569–2570</td><td className="border border-gray-200 px-2 py-1.5">17,500</td><td className="border border-gray-200 px-2 py-1.5 font-mono font-semibold">875 ← ปัจจุบัน</td></tr>
            <tr><td className="border border-gray-200 px-2 py-1.5">2571–2572</td><td className="border border-gray-200 px-2 py-1.5">20,000</td><td className="border border-gray-200 px-2 py-1.5 font-mono">1,000</td></tr>
            <tr><td className="border border-gray-200 px-2 py-1.5">2573 ขึ้นไป</td><td className="border border-gray-200 px-2 py-1.5">23,000</td><td className="border border-gray-200 px-2 py-1.5 font-mono">1,150</td></tr>
          </tbody>
        </table>
      </div>

      <H3>ตัวอย่าง (ปี 2569)</H3>
      <Code>{`ค่าจ้าง 14,000 → min(14,000, 17,500) × 5% = 700
ค่าจ้าง 30,000 → min(30,000, 17,500) × 5% = 875 (ติดเพดาน)`}</Code>

      <Refs items={[
        <>พ.ร.บ. ประกันสังคม <strong>ม.46</strong> — อัตราเงินสมทบ</>,
        <>กฎกระทรวงปรับเพดาน 2569 — 15,000 → 17,500 บาท/เดือน</>,
      ]}/>
    </SectionCard>
  );
}

// === C. กองทุนสำรองเลี้ยงชีพ ===
function SectionPF() {
  return (
    <SectionCard id="pf" icon={<PiggyBank className="w-5 h-5 text-brand-700" />} title="C. กองทุนสำรองเลี้ยงชีพ (PF)">
      <H3>หลักการ</H3>
      <p className="text-sm text-gray-700">
        เป็นกองทุน <strong>สมัครใจ</strong> — นายจ้างและลูกจ้างจ่ายตามอัตราที่ตกลงกัน
        (เช่น ฝ่ายละ 3–5%) เพื่อสะสมไว้ใช้หลังเกษียณ.
        เงินสมทบของลูกจ้าง <strong>นำมาลดหย่อนภาษีได้</strong> ตามเพดาน ม.65 ตรี
      </p>

      <H3>สูตรการหัก</H3>
      <Code>{`PF เดือนนี้ = ค่าจ้าง × pf_rate%        (rate ของแต่ละคน, ตั้งเอง)`}</Code>

      <H3>เพดานลดหย่อนภาษี (ม.65 ตรี)</H3>
      <Code>{`PF ที่ลดหย่อนได้ = min(จ่ายจริง,
                       เงินได้ × 15%,
                       500,000 บาท/ปี)`}</Code>
      <p className="text-xs text-gray-500 mt-2">
        ⚠️ 15% นับรวมกับ RMF/SSF/ก.บ.ข. ตามกฎหมาย แต่ระบบนี้ track เฉพาะ PF —
        ส่วน RMF/SSF พนักงานลดหย่อนเองตอนยื่น ภงด.91
      </p>

      <H3>ตัวอย่าง</H3>
      <Code>{`เงินเดือน 30,000 · PF rate 5%
  PF/เดือน = 30,000 × 5% = 1,500
  PF/ปี = 18,000

  ลดหย่อนภาษี = min(18,000, 30,000×12×15%=54,000, 500k) = 18,000 ✓`}</Code>

      <Refs items={[
        <>ประมวลรัษฎากร <strong>ม.65 ตรี (3)</strong> — เพดานลดหย่อน PF</>,
        <>พ.ร.บ. กองทุนสำรองเลี้ยงชีพ 2530</>,
      ]}/>
    </SectionCard>
  );
}

// === D. ค่าจ้างรายวัน / รายชั่วโมง ===
function SectionDailyRate() {
  return (
    <SectionCard id="daily-rate" icon={<Clock className="w-5 h-5 text-brand-700" />} title="D. ค่าจ้างรายวัน / รายชั่วโมง">
      <H3>หลักการ</H3>
      <p className="text-sm text-gray-700">
        อัตราค่าจ้างรายวันและรายชั่วโมงใช้ในการคำนวณ OT, ค่าทำงานวันหยุด,
        ค่าชดเชย ฯลฯ — คำนวณจาก base_salary หรือ daily_rate ของพนักงาน
      </p>

      <H3>สูตร</H3>
      <Code>{`พนักงานรายเดือน:
  อัตรารายวัน  = เงินเดือน / 30        (เฉลี่ยตามวันปฏิทิน)
  อัตรารายชั่วโมง = อัตรารายวัน / hours_per_workday

พนักงานรายวัน:
  อัตรารายวัน  = daily_rate (ที่บันทึกไว้)
  อัตรารายชั่วโมง = daily_rate / hours_per_workday`}</Code>

      <H3>ตัวอย่าง</H3>
      <Code>{`รายเดือน 30,000 · hours_per_workday = 8
  อัตรารายวัน  = 30,000 / 30 = 1,000
  อัตรารายชั่วโมง = 1,000 / 8 = 125

รายวัน 500 · hours_per_workday = 8
  อัตรารายชั่วโมง = 500 / 8 = 62.5`}</Code>

      <p className="text-xs text-gray-500 mt-2">
        ⚠️ ทำไมต้องหาร 30? — เป็น industry standard ที่ใช้กันมานาน (เฉลี่ยวันปฏิทิน
        รวมเสาร์อาทิตย์) ซึ่งทำให้อัตรารายวันต่ำกว่าหาร 26 (วันทำงานจริง) ~14%
        แต่ก็เป็นวิธีที่กรมสวัสดิการฯ ยอมรับ
      </p>

      <Refs items={[
        <>practice มาตรฐานอุตสาหกรรมไทย (กฎหมายไม่ได้กำหนดตรงๆ)</>,
        <>ใช้สำหรับ OT (×1.5, ×2.0, ×3.0) + ค่าชดเชย §118 + final pay</>,
      ]}/>
    </SectionCard>
  );
}

// === E. OT 3 ประเภท ===
function SectionOT() {
  return (
    <SectionCard id="ot" icon={<Zap className="w-5 h-5 text-brand-700" />} title="E. ค่าล่วงเวลา (OT) — 3 ประเภท">
      <H3>หลักการ</H3>
      <p className="text-sm text-gray-700">
        ประเภท OT ถูก <strong>derive อัตโนมัติ</strong> จากวัน (วันทำงานปกติ / วันหยุดประจำสัปดาห์ / วันหยุดประเพณี)
        + ช่วงเวลา (ในกะ / นอกกะ) — ไม่ให้พนักงานเลือกเอง (กันใส่ผิด)
      </p>

      <H3>อัตราการคูณ</H3>
      <div className="overflow-x-auto">
        <table className="text-sm w-full border-collapse">
          <thead className="bg-gray-50 text-xs text-gray-600">
            <tr>
              <th className="border border-gray-200 px-2 py-1.5 text-left">ประเภท</th>
              <th className="border border-gray-200 px-2 py-1.5 text-left">เงื่อนไข</th>
              <th className="border border-gray-200 px-2 py-1.5 text-left">อัตรา</th>
              <th className="border border-gray-200 px-2 py-1.5 text-left">มาตรา</th>
            </tr>
          </thead>
          <tbody className="text-gray-700">
            <tr><td className="border border-gray-200 px-2 py-1.5">OT-1</td><td className="border border-gray-200 px-2 py-1.5">วันทำงานปกติ — เกินเวลาทำงาน</td><td className="border border-gray-200 px-2 py-1.5 font-mono">×1.5</td><td className="border border-gray-200 px-2 py-1.5 text-xs">ม.61</td></tr>
            <tr><td className="border border-gray-200 px-2 py-1.5">OT-2</td><td className="border border-gray-200 px-2 py-1.5">วันหยุด — ทำงานในเวลาปกติ (รายเดือน: ได้ค่าจ้างวันหยุดอยู่แล้ว = +1.0)</td><td className="border border-gray-200 px-2 py-1.5 font-mono">×1.0–2.0</td><td className="border border-gray-200 px-2 py-1.5 text-xs">ม.62</td></tr>
            <tr><td className="border border-gray-200 px-2 py-1.5">OT-3</td><td className="border border-gray-200 px-2 py-1.5">วันหยุด — ทำงานเกินเวลาปกติ</td><td className="border border-gray-200 px-2 py-1.5 font-mono">×3.0</td><td className="border border-gray-200 px-2 py-1.5 text-xs">ม.64</td></tr>
          </tbody>
        </table>
      </div>

      <H3>สูตรการคิดเงิน</H3>
      <Code>{`OT pay = ชั่วโมง × อัตรารายชั่วโมง × multiplier

ระบบคิดต่อ "นาที" (ไม่ปัดเศษ):
  อัตราต่อนาที = เงินเดือน / 30 / 8 / 60`}</Code>

      <H3>เพดาน + กฎกันโกง</H3>
      <ul className="text-sm text-gray-700 list-disc pl-6 space-y-0.5">
        <li><strong>36 ชั่วโมง/สัปดาห์</strong> — เพดาน OT ตามกฎหมาย</li>
        <li>OT ข้ามเที่ยงคืน → ตัดเป็น 2 ช่วง ก่อน/หลัง 00:00 (ประเภทอาจต่างกัน)</li>
        <li>OT clamp ให้อยู่ <strong>นอกเวลากะ</strong> (ทับกะไม่ได้)</li>
        <li>OT pay ไม่เกินเวลาที่<strong>สแกนออกจริง</strong> (กันโป๊ะชั่วโมง). ถ้าไม่สแกน → ได้ตามเวลาในกะ</li>
        <li>OT บนวันลา/ขาด = block</li>
        <li>OT ซ้อนช่วง 2 ใบ = ห้าม</li>
      </ul>

      <Refs items={[
        <>พ.ร.บ.คุ้มครองแรงงาน <strong>ม.61</strong> (OT วันทำงาน), <strong>ม.62</strong> (วันหยุด), <strong>ม.64</strong> (OT วันหยุด)</>,
        <>ม.26 — เพดาน OT 36 ชม./สัปดาห์</>,
      ]}/>
    </SectionCard>
  );
}

// === F. เบี้ยกะกลางคืน ===
function SectionNight() {
  return (
    <SectionCard id="night" icon={<Moon className="w-5 h-5 text-brand-700" />} title="F. เบี้ยกะกลางคืน">
      <H3>หลักการ</H3>
      <p className="text-sm text-gray-700">
        พนักงานที่ทำงานในช่วง "กะกลางคืน" (เช่น 22:00–06:00) ได้เบี้ยเพิ่ม
        ตามที่บริษัทตั้งไว้ — กฎหมายไม่บังคับ แต่เป็นมาตรฐานอุตสาหกรรม.
        คิดเฉพาะ "ชั่วโมงที่อยู่ในหน้าต่างกลางคืน" ของกะนั้น
      </p>

      <H3>สูตร</H3>
      <Code>{`ชั่วโมงกลางคืน = overlap(เวลาทำงาน, night_window) ของกะนั้น

  รูปแบบ percent: เบี้ย = ชม.กลางคืน × อัตรารายชั่วโมง × premium%
  รูปแบบ flat:    เบี้ย = premium_value (ก้อนเดียวต่อกะ)`}</Code>

      <H3>ตัวอย่าง</H3>
      <Code>{`กะ 22:00–06:00 (paid_hours = 8)
night_window = 22:00–06:00 (ทั้งกะ)
premium = 25% percent · hourlyRate = 125
  ชม.กลางคืน = 8
  เบี้ย = 8 × 125 × 25% = 250 บาท/กะ`}</Code>

      <p className="text-xs text-gray-500 mt-2">
        ตั้งค่าได้ใน <strong>ตั้งค่าระบบ → กะการทำงาน</strong> ต่อกะ (percent / flat / none)
      </p>

      <Refs items={[
        <>กฎหมายไม่บังคับ — practice อุตสาหกรรม</>,
        <>คิดในกระบวนการ reconcile แล้ว fold เข้า additions ของ payroll</>,
      ]}/>
    </SectionCard>
  );
}

// === G. การลา ===
function SectionLeave() {
  return (
    <SectionCard id="leave" icon={<Plane className="w-5 h-5 text-brand-700" />} title="G. การลา (โควต้า + กฎปีแรก §30)">
      <H3>ประเภทการลา</H3>
      <ul className="text-sm text-gray-700 list-disc pl-6 space-y-0.5">
        <li><strong>ลาป่วย (sick)</strong> — สูงสุด 30 วัน/ปี (จ่ายค่าจ้าง) ตาม ม.32</li>
        <li><strong>ลากิจ (personal)</strong> — สูงสุด 3 วัน/ปี (จ่ายค่าจ้าง) ตาม ม.34</li>
        <li><strong>ลาพักร้อน (vacation)</strong> — 6 วัน/ปี ขั้นต่ำ ตาม ม.30</li>
        <li><strong>ลาคลอด (maternity)</strong> — 98 วัน ตาม ม.41</li>
        <li><strong>ลาไม่รับค่าจ้าง (unpaid)</strong> — ไม่จำกัด แต่ต้องอนุมัติ</li>
      </ul>

      <H3>กฎปีแรก (ม.30) ⭐</H3>
      <p className="text-sm text-gray-700">
        ลาพักร้อนตามกฎหมาย <strong>มีสิทธิ์เมื่อทำงานครบ 1 ปี</strong> เท่านั้น —
        ดังนั้นพนักงานปีแรกจะถูก <strong>ปิดสิทธิ์ลาพักร้อนโดยอัตโนมัติ</strong>
        เว้นแต่ Owner ตั้ง <code className="text-xs bg-gray-100 px-1 rounded">vacation_first_year_eligible = true</code>
        (ให้สิทธิ์ทันที — เป็นนโยบายบริษัท ดีกว่ากฎหมาย)
      </p>

      <H3>Prorate ปีแรก (เข้ากลางปี)</H3>
      <p className="text-sm text-gray-700">
        ถ้า <code className="text-xs bg-gray-100 px-1 rounded">prorate_on_join = true</code>
        + พนักงานเข้ากลางปี → quota จะลดตามอัตราส่วน <strong>วันที่ active / 365</strong>
      </p>
      <Code>{`พนักงานเริ่ม 1 ก.ค. (active 184/365 วัน) · vacation_quota=6
  → quota จริง = 6 × 184/365 ≈ 3.0 วัน`}</Code>

      <H3>Carry-over (ยกยอด)</H3>
      <p className="text-sm text-gray-700">
        สิ้นปีถ้าใช้ไม่หมด → ยกยอดได้ตาม <code className="text-xs bg-gray-100 px-1 rounded">carry_over_max</code>
        (ตั้งใน leave_package) — 0 = ไม่ยกยอด
      </p>

      <Refs items={[
        <>พ.ร.บ.คุ้มครองแรงงาน <strong>ม.30</strong> (ลาพักร้อน 6 วัน, ต้องครบ 1 ปี)</>,
        <><strong>ม.32</strong> (ลาป่วย), <strong>ม.34</strong> (ลากิจ), <strong>ม.41</strong> (ลาคลอด)</>,
      ]}/>
    </SectionCard>
  );
}

// === H. ค่าชดเชยเลิกจ้าง ===
function SectionSeverance() {
  return (
    <SectionCard id="severance" icon={<UserMinus className="w-5 h-5 text-brand-700" />} title="H. ค่าชดเชยเลิกจ้าง §118">
      <H3>หลักการ</H3>
      <p className="text-sm text-gray-700">
        เฉพาะ <strong>เลิกจ้างโดยนายจ้าง</strong> (terminated / retired / contract_end ≥ 120 วัน) เท่านั้น —
        ลูกจ้าง<strong>ลาออกเอง = ไม่ได้</strong>
      </p>
      <p className="text-sm text-gray-700 mt-2">
        <strong className="text-rose-700">ข้อยกเว้น ม.119:</strong> ถ้าเลิกจ้างเพราะลูกจ้าง
        <em>ทุจริต / จงใจให้เสียหาย / ขาดงาน 3 วันติด / ฝ่าฝืนระเบียบหลังเตือนเป็นหนังสือ</em>
        → <strong>ไม่มีค่าชดเชย + ไม่ต้องบอกกล่าวล่วงหน้า</strong> (เลือก reason
        <code className="text-xs bg-gray-100 px-1 rounded">terminated_for_cause</code> ในระบบ)
      </p>

      <H3>ตารางค่าชดเชย ม.118</H3>
      <div className="overflow-x-auto">
        <table className="text-sm w-full border-collapse">
          <thead className="bg-gray-50 text-xs text-gray-600">
            <tr>
              <th className="border border-gray-200 px-2 py-1.5 text-left">อายุงาน</th>
              <th className="border border-gray-200 px-2 py-1.5 text-left">ค่าชดเชย (วันของค่าจ้าง)</th>
            </tr>
          </thead>
          <tbody className="text-gray-700">
            <tr><td className="border border-gray-200 px-2 py-1.5">{'< 120 วัน'}</td><td className="border border-gray-200 px-2 py-1.5 font-mono">0</td></tr>
            <tr><td className="border border-gray-200 px-2 py-1.5">120 วัน – 1 ปี</td><td className="border border-gray-200 px-2 py-1.5 font-mono">30 วัน</td></tr>
            <tr><td className="border border-gray-200 px-2 py-1.5">1 – 3 ปี</td><td className="border border-gray-200 px-2 py-1.5 font-mono">90 วัน</td></tr>
            <tr><td className="border border-gray-200 px-2 py-1.5">3 – 6 ปี</td><td className="border border-gray-200 px-2 py-1.5 font-mono">180 วัน</td></tr>
            <tr><td className="border border-gray-200 px-2 py-1.5">6 – 10 ปี</td><td className="border border-gray-200 px-2 py-1.5 font-mono">240 วัน</td></tr>
            <tr><td className="border border-gray-200 px-2 py-1.5">10 – 20 ปี</td><td className="border border-gray-200 px-2 py-1.5 font-mono">300 วัน</td></tr>
            <tr><td className="border border-gray-200 px-2 py-1.5">≥ 20 ปี</td><td className="border border-gray-200 px-2 py-1.5 font-mono">400 วัน</td></tr>
          </tbody>
        </table>
      </div>

      <H3>Final Pay</H3>
      <Code>{`final pay = ค่าชดเชย (วัน × อัตรารายวัน)
          + ค่าลาพักร้อนสะสมที่ยังไม่ใช้ (วัน × อัตรารายวัน)
          + ค่าบอกกล่าวล่วงหน้า (กรณีไม่ได้แจ้งล่วงหน้า) ม.17`}</Code>

      <H3>ตัวอย่าง</H3>
      <Code>{`เลิกจ้าง · อายุงาน 5 ปี · เงินเดือน 30,000 · ลาพักร้อนเหลือ 3 วัน
  อัตรารายวัน = 30,000/30 = 1,000
  ค่าชดเชย    = 180 × 1,000 = 180,000
  ลาพักร้อน   = 3 × 1,000 = 3,000
  ค่าบอกกล่าว = 30 × 1,000 = 30,000  (แจ้งกระชั้นชิด 1 งวด)
  ─────────────────────
  final pay  = 213,000 บาท`}</Code>

      <H3>ภาษีค่าชดเชย (ม.42(17))</H3>
      <Code>{`เพดานยกเว้นภาษี = MIN(300 วัน × อัตรารายวัน, 300,000)

  ≤ เพดาน → ยกเว้นภาษีทั้งก้อน (ไม่เข้าฐาน)
  > เพดาน → ส่วนเกิน เสียภาษีเป็นเงินได้ปกติ

ลาพักร้อนสะสม + ค่าบอกกล่าว = เงินได้ปกติเต็มจำนวน
  (ไม่ใช่ "ค่าชดเชย" → ไม่ได้รับการยกเว้น)`}</Code>
      <p className="text-sm text-gray-700 mt-2">
        ระบบ feed 2 ก้อนเข้า payroll แยกกัน — <strong>ก้อน exempt</strong> (ไม่หักภาษี)
        และ <strong>ก้อน taxable</strong> (เข้าฐานภาษีเงินได้ปกติ).
        SSO/PF ไม่หักจากเงินเลิกจ้างทั้ง 2 ก้อน (เป็นเงินได้ออกจากงาน ไม่ใช่ค่าจ้างประจำ)
      </p>
      <p className="text-xs text-gray-500 mt-2">
        หมายเหตุ: หากอายุงาน ≥5 ปี + severance ส่วนเกินมาก พนักงานเลือกใช้ใบแนบ ภงด.91/90
        แยกคำนวณภาษีตอนยื่นได้ (ม.48(5)) — เป็นทางเลือกของผู้เสียภาษี ไม่กระทบการหัก ณ ที่จ่าย
      </p>

      <Refs items={[
        <>พ.ร.บ.คุ้มครองแรงงาน <strong>ม.118</strong> — ตารางค่าชดเชย</>,
        <><strong>ม.119</strong> — กรณี for-cause (ทุจริต/จงใจ): 0 ชดเชย</>,
        <><strong>ม.17</strong> — ค่าบอกกล่าวล่วงหน้า</>,
        <>ประมวลรัษฎากร <strong>ม.42(17)</strong> — ยกเว้นภาษีค่าชดเชย ≤ 300 วัน / 300,000</>,
      ]}/>
    </SectionCard>
  );
}

// === I. เงินหัก §76 ===
function SectionDeductions() {
  return (
    <SectionCard id="deductions" icon={<Scissors className="w-5 h-5 text-brand-700" />} title="I. เงินหัก §76">
      <H3>หลักการ</H3>
      <p className="text-sm text-gray-700">
        นายจ้างหักเงินเดือนได้เฉพาะ <strong>รายการที่กฎหมายอนุญาต</strong> — และต้อง
        <strong>มีหนังสือยินยอม</strong>จากลูกจ้างสำหรับบางประเภท
      </p>

      <H3>รายการที่หักได้</H3>
      <ul className="text-sm text-gray-700 list-disc pl-6 space-y-0.5">
        <li><strong>ภาษีหัก ณ ที่จ่าย</strong> — ตามกฎหมาย ไม่ต้องยินยอม</li>
        <li><strong>ประกันสังคม + กบช.</strong> — ตามกฎหมาย</li>
        <li><strong>PF</strong> — ตามที่ตกลงสมัครเข้ากองทุน</li>
        <li><strong>หนี้สหกรณ์ / กยศ. / สินเชื่อ</strong> — ต้องมีคำสั่งศาล หรือ ยินยอมเป็นหนังสือ</li>
        <li><strong>เงินประกัน / ค่าเสียหาย / ค่าปรับ</strong> — ต้องยินยอม + ตามเพดาน §76</li>
      </ul>

      <H3>เพดาน §76 (สำคัญ)</H3>
      <Code>{`เงินประกัน + ค่าปรับ + ชดใช้ความเสียหาย
รวมกันไม่เกิน 10% ของค่าจ้าง

ส่วนที่หักไม่ได้ในงวดนี้ → ยกไปงวดถัดไป (carry-over)`}</Code>

      <H3>ลำดับการหัก (เมื่อเงินไม่พอ)</H3>
      <ol className="text-sm text-gray-700 list-decimal pl-6 space-y-0.5">
        <li>ภาษี / ประกันสังคม / PF (บังคับ)</li>
        <li>หนี้ตามคำสั่งศาล / กยศ.</li>
        <li>เงินกู้บริษัท / เงินยืม</li>
        <li>เงินประกัน / ค่าเสียหาย (เพดาน 10%)</li>
        <li>เงินสุทธิ <strong>ต้องไม่ติดลบ</strong></li>
      </ol>

      <H3>สถานะปัจจุบัน</H3>
      <ul className="text-sm text-gray-700 list-disc pl-6 space-y-0.5">
        <li>✅ <strong>Net floor</strong> — เงินสุทธิห้ามติดลบ (clamp ≥ 0 อัตโนมัติ)</li>
        <li>✅ <strong>Priority</strong> — statutory ก่อน → loan → other (ผ่าน lib/deduction-resolver)</li>
        <li>✅ <strong>§76 cap 10%</strong> — pure function พร้อมใช้ (ดูใน calculation_log)</li>
        <li>⏳ <strong>Carry-over table</strong> — ส่วนหักไม่ได้ ยกไปงวดถัดไป (ขั้นปรับปรุง)</li>
      </ul>

      <Refs items={[
        <>พ.ร.บ.คุ้มครองแรงงาน <strong>ม.76</strong> — ข้อจำกัดการหักค่าจ้าง</>,
      ]}/>
    </SectionCard>
  );
}

// === J. YTD + true-up ===
function SectionYTD() {
  return (
    <SectionCard id="ytd" icon={<History className="w-5 h-5 text-brand-700" />} title="J. ยอด YTD + true-up เดือน ธ.ค.">
      <H3>YTD คืออะไร</H3>
      <p className="text-sm text-gray-700">
        Year-To-Date = ยอด <strong>สะสมตั้งแต่ต้นปี</strong> ของรายได้, ภาษี, ประกันสังคม, PF.
        ระบบเก็บใน <code className="text-xs bg-gray-100 px-1 rounded">ytd_accumulator</code>
        อัปเดตอัตโนมัติทุกครั้งที่ปิดงวด (lock)
      </p>

      <H3>ทำไมต้องเก็บ YTD</H3>
      <ul className="text-sm text-gray-700 list-disc pl-6 space-y-0.5">
        <li>วิธีคำนวณภาษี <strong>Cumulative-Average</strong> ต้องใช้ taxableYtd + taxPaidYtd</li>
        <li>ตรวจสอบสะสมต่อปี (50 ทวิ / ภงด.91)</li>
        <li>คำนวณเพดานปีของ SSO + PF ที่ลดหย่อนได้</li>
      </ul>

      <H3>True-up เดือน ธันวาคม</H3>
      <p className="text-sm text-gray-700">
        เดือน ธ.ค. (m=12) สูตรเฉลี่ยสะสมจะกลายเป็น <strong>การปรับยอดจริงปลายปี</strong>:
      </p>
      <Code>{`projected_annual = ยอดสะสม × 12 / 12 = ยอดสะสมจริง
ภาษีที่ควรหักทั้งปี = คำนวณจากยอดสะสมจริง
ภาษี ธ.ค. = ภาษีทั้งปี − ที่หักไปแล้ว

→ ถ้าเดือนก่อนๆ หักเกิน: ภาษี ธ.ค. ลดลง (อาจถึง 0)
→ ถ้าหักขาด: ภาษี ธ.ค. มากขึ้น (catch up)`}</Code>

      <p className="text-xs text-gray-500 mt-2">
        ระบบ <strong>ไม่คืนภาษีระหว่างปี</strong> — เดือนที่ควรคืน → set เป็น 0 ไว้ก่อน
        ส่วนเกินจะคืนรวมตอน ธ.ค. หรือยื่น ภงด.91 ปลายปี
      </p>

      <Refs items={[
        <>แนวปฏิบัติของกรมสรรพากร — Cumulative Average</>,
        <>ประมวลรัษฎากร <strong>ม.59</strong> — ภงด.1 รายเดือน, <strong>ม.58</strong> — ภงด.1ก</>,
      ]}/>
    </SectionCard>
  );
}
